import type { AgentModelConfigurationType } from "@app/types/assistant/agent";
import { SUPPORTED_MODEL_CONFIGS } from "@app/types/assistant/models/models";
import type {
  ModelConfigurationType,
  ReasoningEffort,
  SupportedModel,
} from "@app/types/assistant/models/types";

export function getSupportedModelConfigs(): readonly ModelConfigurationType[] {
  return SUPPORTED_MODEL_CONFIGS;
}

/**
 * Get a specific supported model configuration by model/provider ID.
 * Returns null if the model configuration is not found.
 */
export function getSupportedModelConfig(
  supportedModel: SupportedModel | AgentModelConfigurationType
): ModelConfigurationType | null {
  const configs = getSupportedModelConfigs();
  const config = configs.find(
    (m) =>
      m.modelId === supportedModel.modelId &&
      m.providerId === supportedModel.providerId
  );

  return config ?? null;
}

/**
 * Get the reasoning effort a model configuration actually runs at: its explicit effort, or the
 * effort its model defaults to when it sets none. Falls back to "none" for a model that is no
 * longer supported.
 */
export function getEffectiveReasoningEffort(
  model: AgentModelConfigurationType
): ReasoningEffort {
  return (
    model.reasoningEffort ??
    getSupportedModelConfig(model)?.defaultReasoningEffort ??
    "none"
  );
}

/**
 * Get a specific model configuration by model ID only.
 */
export function getModelConfigByModelId(
  modelId: string
): ModelConfigurationType | undefined {
  const configs = getSupportedModelConfigs();
  return configs.find((m) => m.modelId === modelId);
}
